import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ExternalLink } from "lucide-react";

import {
  ContactId,
  OpportunityId,
  OrganizationId,
} from "@goose/schema/Salesforce";

import { Breadcrumbs } from "~/components/admin/Breadcrumbs";
import { DetailsTable } from "~/components/admin/DetailsTable";

import {
  ContactIcon,
  EvaluationIcon,
  OpportunityIcon,
  OrganizationIcon,
  RequirementSetIcon,
  SalesforceUserIcon,
} from "~/components/admin/Icons";

import { OrganizationEditDialogButton } from "~/components/admin/OrganizationEditDialogButton";
import { PageHeader } from "~/components/admin/PageHeader";
import { QuickNav } from "~/components/admin/QuickNav";
import { RecentContacts } from "~/components/admin/RecentContacts";
import { RecentOpportunities } from "~/components/admin/RecentOpportunities";
import { SalesforceUsersTable } from "~/components/admin/SalesforceUsersTable";
import { StatCard } from "~/components/admin/StatCard";
import { QueryState } from "~/components/QueryState";
import { TRPCClientErrorDisplay } from "~/components/TRPCClientErrorDisplay";
import { Button } from "~/components/ui/Button";
import { Spinner } from "~/components/ui/Spinner";
import { useTRPC } from "~/lib/trpc";

export const Route = createFileRoute(
  "/_authenticated/admin/organizations/$organizationId/",
)({
  component: OrganizationDetailPage,
});

interface OrganizationUsersProps {
  organizationId: OrganizationId;
}

function OrganizationUsers({ organizationId }: OrganizationUsersProps) {
  const trpc = useTRPC();

  const query = useQuery(
    trpc.salesforce.users.list.queryOptions(
      {
        organizationId,
      },
      { trpc: { context: { skipBatch: true } } },
    ),
  );

  return (
    <QueryState
      result={query}
      loading={<Spinner size="lg" />}
      error={(error) => <TRPCClientErrorDisplay error={error} />}
    >
      {(data) => (
        <SalesforceUsersTable organizationId={organizationId} users={data} />
      )}
    </QueryState>
  );
}

function OrganizationQuickNav({
  organizationId,
}: {
  organizationId: OrganizationId;
}) {
  const navigate = useNavigate();

  const goToContact = async (contactId: ContactId) => {
    await navigate({
      to: "/admin/organizations/$organizationId/contacts/$contactId",
      params: { organizationId, contactId },
    });
  };

  const goToOpportunity = async (opportunityId: OpportunityId) => {
    await navigate({
      to: "/admin/organizations/$organizationId/opportunities/$opportunityId",
      params: { organizationId, opportunityId },
    });
  };

  return (
    <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
      <QuickNav
        label="Contact"
        placeholder="003XXXXXXXXXXXXXXX"
        icon={ContactIcon}
        schema={ContactId}
        onSubmit={goToContact}
      />
      <QuickNav
        label="Opportunity"
        placeholder="006XXXXXXXXXXXXXXX"
        icon={OpportunityIcon}
        schema={OpportunityId}
        onSubmit={goToOpportunity}
      />
    </div>
  );
}

function OrganizationDetailPage() {
  const trpc = useTRPC();
  const { organizationId } = Route.useParams();

  const query = useQuery(
    trpc.salesforce.organizations.get.queryOptions({
      id: organizationId,
    }),
  );

  return (
    <QueryState
      result={query}
      loading={
        <div className="flex justify-center items-center py-24">
          <Spinner size="lg" />
        </div>
      }
      error={(error) => <TRPCClientErrorDisplay error={error} />}
    >
      {(organization) => (
        <div className="space-y-6">
          <PageHeader
            breadcrumbs={
              <Breadcrumbs>
                <Link to="/admin">Admin</Link>
                <Link to="/admin/organizations">Organizations</Link>
                <span>{organization.name}</span>
              </Breadcrumbs>
            }
            icon={OrganizationIcon}
            title={organization.name}
            actions={
              <>
                <Button
                  as="a"
                  href={organization.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="outline"
                  size="sm"
                >
                  <ExternalLink className="mr-2 h-4 w-4" />
                  Open in Salesforce
                </Button>
                <OrganizationEditDialogButton
                  organization={organization}
                  size="sm"
                />
              </>
            }
          />

          <div className="grid grid-cols-1 gap-3 md:grid-cols-3 xl:grid-cols-5">
            <StatCard
              label="Users"
              icon={<SalesforceUserIcon className="h-7 w-7" />}
              value={organization.usersCount}
            />
            <StatCard
              label="Opportunities"
              value={organization.opportunitiesCount}
              icon={<OpportunityIcon className="h-7 w-7" />}
            />
            <StatCard
              label="Req Sets"
              value={organization.requirementSetsCount}
              icon={<RequirementSetIcon className="h-7 w-7" />}
            />
            <StatCard
              label="Contacts"
              value={organization.contactsCount}
              icon={<ContactIcon className="h-7 w-7" />}
            />
            <StatCard
              label="Evaluations"
              value={organization.evaluationsCount}
              icon={<EvaluationIcon className="h-7 w-7" />}
            />
          </div>

          <DetailsTable
            details={[
              {
                label: "ID",
                value: organization.id,
              },
              {
                label: "Name",
                value: organization.name,
              },
              {
                label: "URL",
                value: (
                  <a
                    href={organization.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-500 hover:text-blue-700"
                  >
                    {organization.url}
                  </a>
                ),
              },
            ]}
          />

          <OrganizationQuickNav organizationId={organizationId} />

          <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
            <RecentContacts organizationId={organizationId} limit={5} />
            <RecentOpportunities organizationId={organizationId} limit={5} />
          </div>

          <OrganizationUsers organizationId={organizationId} />
        </div>
      )}
    </QueryState>
  );
}
